import "./FeeScheduleCard.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import type { ReactNode } from "react";

export type FeeScheduleCardProps = {
  heading: string;
  /** Pricing field names in this section, in display order. */
  fields: string[];
  /** Field name -> human label, from DEFAULT_INVOICE_PRICING_FIELDS. */
  labels: Record<string, string>;
  /** Pricing field names archived for this client (any section). */
  archivedFields: string[];
  /** Renders the editable price input for an active fee type. */
  renderInput: (name: string, label: string) => ReactNode;
  onArchive: (name: string) => void;
  onRestore: (name: string) => void;
  /**
   * Pricing field whose archive/restore request is in flight. Only that
   * row's control is disabled, so a slow request on one fee type does not
   * lock the rest of the schedule.
   */
  pendingField?: string | null;
};

/**
 * One section of the client fee schedule. Active fee types are editable
 * and can be archived; archived fee types are listed below, read-only,
 * with a restore control. Archiving hides the fee type from new invoice
 * line items for this client but keeps its price so restoring is lossless.
 */
const FeeScheduleCard = ({
  heading,
  fields,
  labels,
  archivedFields,
  renderInput,
  onArchive,
  onRestore,
  pendingField = null,
}: FeeScheduleCardProps) => {
  const activeFields = fields.filter((name) => !archivedFields.includes(name));
  const archivedInSection = fields.filter((name) =>
    archivedFields.includes(name),
  );
  const labelFor = (name: string) => labels[name] ?? name;

  return (
    <Card heading={heading} classNames="fee-schedule-card">
      {activeFields.map((name) => (
        <div
          key={name}
          className="fee-schedule-row"
          data-testid={`fee-schedule-row-${name}`}
        >
          {renderInput(name, labelFor(name))}
          <ButtonIcon
            type="button"
            size="small"
            className="fee-schedule-row__action"
            title={`Archive ${labelFor(name)}`}
            aria-label={`Archive ${labelFor(name)}`}
            disabled={pendingField === name}
            onClick={() => onArchive(name)}
          >
            archive
          </ButtonIcon>
        </div>
      ))}

      {archivedInSection.length > 0 && (
        <div className="fee-schedule-card__archived-heading">
          Archived fee types
        </div>
      )}

      {archivedInSection.map((name) => (
        <div
          key={name}
          className="fee-schedule-row fee-schedule-row--archived"
          data-testid={`fee-schedule-row-${name}`}
        >
          <span className="fee-schedule-row__label">{labelFor(name)}</span>
          <span className="fee-schedule-row__status">Archived</span>
          <ButtonIcon
            type="button"
            size="small"
            className="fee-schedule-row__action"
            title={`Restore ${labelFor(name)}`}
            aria-label={`Restore ${labelFor(name)}`}
            disabled={pendingField === name}
            onClick={() => onRestore(name)}
          >
            unarchive
          </ButtonIcon>
        </div>
      ))}
    </Card>
  );
};

export default FeeScheduleCard;
